import {
  type PayrollRosterContract,
  type PayrollRosterContractField,
  type PayrollRosterField,
} from "@/payroll/roster/types";

export const sortedRosterFields = (fields: PayrollRosterField[]) =>
  [...fields].sort((first, second) => first.position - second.position);

// --- Contract (shape governance) -----------------------------------------

// A business is "governed" once its published script exposes a contract; an
// empty contract means the roster shape is still ungoverned.
export const isGovernedContract = (contract: PayrollRosterContract) =>
  Object.keys(contract).length > 0;

// Governed rosters render only the columns the published contract still
// declares: a field the contract dropped disappears from the grid and the save
// payload. The backend already heals removed columns out of the draft it
// returns; this is a belt-and-suspenders client-side guard so a stale field can
// never leak into the table. Ungoverned rosters keep every field untouched.
export const fieldsForContract = (
  fields: PayrollRosterField[],
  contract: PayrollRosterContract,
): PayrollRosterField[] => {
  if (!isGovernedContract(contract)) return fields;
  return fields.filter((field) => field.key in contract);
};

// fixed_option choices are sourced LIVE from the contract when governed. Only
// when the business is ungoverned do we fall back to the field's stored options
// (preserving behavior for businesses without a published contract).
export const contractFieldOptions = (
  fieldKey: string,
  contract: PayrollRosterContract,
  storedOptions: string[],
): string[] => {
  if (!isGovernedContract(contract)) return storedOptions;
  return contract[fieldKey]?.supported_options ?? [];
};

// Turns a field key ("pay_plan") into a human label ("Pay plan").
export const humanizeFieldKey = (key: string): string => {
  const spaced = key.replace(/[_-]+/g, " ").trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

// Builds a roster field wholly derived from the contract (shape is governed).
export const contractFieldToRosterField = (
  key: string,
  contractField: PayrollRosterContractField,
  position: number,
): PayrollRosterField => ({
  key,
  label: humanizeFieldKey(key),
  field_type: contractField.type,
  required: contractField.required,
  options: contractField.supported_options,
  position,
  format: contractField.format,
});

// The columns the roster editor should render. For a GOVERNED business the
// columns are derived from the CONTRACT (its single source of truth for shape),
// but WITHOUT scrambling the established column order the owner is used to:
//   1. Keep the existing roster fields in their current `position` order, minus
//      any the contract no longer declares (that removal behavior stays).
//   2. APPEND any contract fields the roster doesn't yet have — the
//      newly-auto-added columns — at the end, in the contract's own key order,
//      synthesized empty via `contractFieldToRosterField`.
// Existing fields keep their relative order; appended new fields get positions
// after the last existing one. The net effect for governed = the editor
// columns are exactly the contract's fields, established ones first. Ungoverned
// rosters pass through their stored fields (sorted by position), unchanged.
export const contractColumns = (
  rosterFields: PayrollRosterField[],
  contract: PayrollRosterContract,
): PayrollRosterField[] => {
  if (!isGovernedContract(contract)) return sortedRosterFields(rosterFields);

  // Existing fields the contract still declares, in their established order.
  // Display metadata stays stable, while schema rules come from the current
  // published contract so type/required/option changes take effect immediately.
  const existing = sortedRosterFields(rosterFields)
    .filter((field) => field.key in contract)
    .map((field) => {
      const contractField = contract[field.key];
      return {
        ...field,
        field_type: contractField.type,
        required: contractField.required,
        options: contractField.supported_options,
        format: contractField.format,
      };
    });
  const existingKeys = new Set(existing.map((field) => field.key));

  // Contract fields with no roster column yet — appended in contract key order.
  let nextPosition = existing.reduce(
    (max, field) => Math.max(max, field.position),
    -1,
  );
  const appended = Object.keys(contract)
    .filter((key) => !existingKeys.has(key))
    .map((key) => {
      nextPosition += 1;
      return contractFieldToRosterField(key, contract[key], nextPosition);
    });

  return [...existing, ...appended];
};
